<template>
<div id="profileview" class="profileview">
	<div class="profileview__profile">

		<!-- -------- -->
		<!--   NAME   -->
		<!-- -------- -->
		<div class="profile__header">

			<router-link class="header__logo" to="/">
				<div class="logo__outline"></div>
				<div class="logo__wrapper">
					<img src="../assets/iomico_logo_text.svg" 
						class="wrapper__image"/>
				</div>
			</router-link>

			<!-- ------------ -->
			<!--   CONTROLS   -->
			<!-- ------------ -->

			<div class="header__btns">
				<v-btn 
					depressed 
					small
					class="btns__btn print"
					@click="printing()"
				>
					<v-icon dark>mdi-printer</v-icon>
				</v-btn>

				<div class="btns__controls-wrapper">
					<div class="controls-wrapper__controls">

						<div 
							@click="updateActiveFromTab('expertise')"
							class="controls__btn expertise"
							:class="{active: activeFromTab == 'expertise'}"
						>
							expertise
						</div>

						<div 
							@click="updateActiveFromTab('work')"
							class="controls__btn work"
							:class="{active: activeFromTab == 'work'}"
						>
							work
						</div>

						<div 
							@click="updateActiveFromTab('education')"
							class="controls__btn education"
							:class="{active: activeFromTab == 'education'}"
						>
							education
						</div>

					</div>
				</div>
			</div>

		</div>

		<div class="profile__wrapper">
			<div class="wrapper__block-1">


				<!-- ----------- -->
				<!--   PICTURE   -->
				<!-- ----------- -->
				<div class="block-1__section">
					<div class="section__picture">
						<img 
							:src="getPictureUrl" 
							v-bind:alt="profile['first name']"
						/>
					</div>

					<!-- -------- -->
					<!--   NAME   -->
					<!-- -------- -->
					<div class="section__name">
						<div class="name__fname">
							{{profile['first name']}}
						</div>
						<div class="name__lname">
							{{profile['last name']}}
						</div>
						<div class="name__position">{{profile['position']}}</div>
					</div>
				</div>


				<!-- ----------- -->
				<!--   GENERAL   -->
				<!-- ----------- -->
				<div class="block-1__section">

					<div class="section__row">
						<div class="row__label iomico-style-lable small">age</div>
						<div>{{profile['birthday']}}</div>
					</div>


					<div class="section__row">
						<div class="row__label iomico-style-lable small">family</div>
						<div class="row__cell">
							<span>{{profile['family status']}}</span>
							<span v-if="profile['children'] && profile['children'] > 0">
								({{profile['children']}} children)
							</span>
						</div>
					</div>
				</div>


				<!-- ------------- -->
				<!--   LANGUAGES   -->
				<!-- ------------- -->
				<div class="block-1__section">
					<div class="section__label iomico-style-lable">languages</div>
					
					<div class="section__item"
						v-bind:key="langIndex"
						v-for="(language, langIndex) in profile['languages']"
					>
						<div class="item__label">
							<div class="label__lang-name">
								{{language.name}}
							</div>

							<div class="label__total-level">
								{{language.total_level}}
							</div>
						</div>

						<div class="item__body">
							<div class="body__rating"
								v-bind:key="levelIndex"
								v-for="(level, levelIndex) in language['levels']">
								
								<Rating 
									class="rating__level"
									v-model="level[1].rate"
									readonly
									:length="5" />

								<div class="rating__name">{{level[0]}}</div>
							</div>

						</div>
					</div>
				</div>

				<!-- --------- -->
				<!--   HOBBY   -->
				<!-- --------- -->
				<div class="block-1__section">
					<div class="section__label iomico-style-lable">hobby</div>

					<div class="section__tags">
						<span
							class="tags__tag"
							v-bind:key="itemIndex"
							v-for="(item, itemIndex) in profile.hobby"
						>
							{{item}}
						</span>
					</div>

				</div>
			</div>

			<div class="wrapper__block-2">
				<!-- ------------- -->
				<!--               -->
				<!--   EXPERTISE   -->
				<!--               -->
				<!-- ------------- -->
				<div 
					class="block-2__level-1 expertise"
					:class="{hidden: activeFromTab != 'expertise'}"
				>
					<div class="level-1__title expertise">
						<div class="title__text">expertise</div>
					</div>

					<div
						class="level-1__level-2"
					>
						<div class="level-2__title iomico-style-lable">
							about
						</div>

						<div>
							{{profile['about']}}
						</div>
					</div>

					<div
						class="level-1__level-2"
						v-bind:key="expertiseName"
						v-for="(subExpertises, expertiseName) in profile['expertise']"
					>
						<div class="level-2__title iomico-style-lable">
							{{expertiseName}}
						</div>

						<div class="level-2__level-3">
							<div 
								class="level-3__items"
								v-bind:key="subExpertiseName"
								v-for="(subExpertise, subExpertiseName) in subExpertises"
							>
								<div class="items__label">
									<div 
										v-if="subExpertiseName != 'skills'" 
										class="label__text"
									>
										{{subExpertiseName}}
									</div>
								</div>

								<div 
									class="items__item"
									v-bind:key="itemIndex"
									v-for="(itemObj, itemIndex) in subExpertise"
								>
									<Rating 
										class="item__rating"
										v-model="itemObj[1].rate"
										readonly
										:length="5" />

									<div 
										class="item__text"
									>
										{{itemObj[0]}}
									</div>

								</div>

							</div>
						</div>
					</div>
				</div>


				<!-- -------- -->
				<!--          -->
				<!--   WORK   -->
				<!--          -->
				<!-- -------- -->
				<div 
					class="block-2__level-1 work"
					:class="{hidden: activeFromTab != 'work'}"
				>
					<div class="level-1__title work">
						<div class="title__text">work</div>
					</div>

					<div
						class="level-1__level-2"
						v-bind:key="workIndex"
						v-for="(work, workIndex) in profile['work']"
					>
						<div class="level-2__header">

							<div class="header__date">
								<span>{{work.period.from}}</span>
								<span class="date__dash">-</span>
								<span>{{work.period.to}}</span>
							</div>

							<div class="header__company">
								{{work.company}}
							</div>

						</div>

						<div class="level-2__block">
							<div 
								class="block__row position"
								v-if="work.position"
							>
								<div class="row__label iomico-style-lable">Position</div>
								<div class="row__text">{{work.position}}</div>
							</div>

							<div 
								class="block__row project-name"
								v-if="work['project_name']"
							>
								<div class="row__label iomico-style-lable">Project Name</div>
								<div class="row__text">{{work['project_name']}}</div>
							</div>

							<div 
								v-if="work['project_description']"
								class="block__row project-descr"
							>
								<div class="row__label iomico-style-lable">Description</div>
								<div class="row__text">{{work['project_description']}}</div>
							</div>
						</div>


						<div class="level-2__block">
							<div
								class="block__expertise"
								v-bind:key="expertiseName"
								v-for="(subExpertises, expertiseName) in work['expertise']"
							>
								<div class="expertise__label">
									<div class="iomico-style-lable">
										{{expertiseName}}
									</div>
								</div>

								<div 
									class="expertise__subexpertise"
									v-bind:key="subExpertiseIndex"
									v-for="(subExpertise, subExpertiseIndex) in subExpertises"
								>
									<div
										class="subexpertise__block"
										v-if="subExpertise[2].enabled"
									>
										<div class="block__dot"></div>
										<div class="block__label">
											{{subExpertise[0]}}
										</div>
										<div class="block__tags">
											<span 
												class="tags__tag"
												v-bind:key="subExpertiseItemIndex"
												v-for="(subExpertiseItem, subExpertiseItemIndex) in subExpertise[1]"
											>
												{{subExpertiseItem}}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- ------------- -->
				<!--               -->
				<!--   EDUCATION   -->
				<!--               -->
				<!-- ------------- -->
				<div 
					class="block-2__level-1 education"
					:class="{hidden: activeFromTab != 'education'}"
				>
					<div class="level-1__title education">
						<div class="title__text">education</div>
					</div>

					<div
						class="level-1__level-2"
						v-bind:key="schoolIndex"
						v-for="(school, schoolIndex) in profile['education']"
					>
						<div class="level-2__header">

							<div class="header__date">
								<span>{{ school['attended dates'].from }}</span>
								<span class="date__dash">-</span>
								<span>{{ school['attended dates'].to }}</span>
							</div>

							<div class="header__school-name">
								{{ school['school name'] }}
							</div>
						</div>

						<div class="level-2__block">
							<div 
								class="block__row location"
								v-if="school['location']['country']"
							>
								<div class="row__label iomico-style-lable">
									Location
								</div>
								<div class="row__text">
									<span class="text__country">
										{{school['location']['country']}}
									</span>
									<span class="text__divider">/</span>
									<span class="text__city">
										{{school['location']['city']}}
									</span>
								</div>
							</div>

							<div 
								class="block__row degree"
								v-if="school['degree']"
							>
								<div class="row__label iomico-style-lable">
									Degree
								</div>
								<div class="row__text">
									{{school['degree']}}
								</div>
							</div>

							<div 
								class="block__row academic-honor"
								v-if="school['academic honor']"
							>
								<div class="row__label iomico-style-lable">
									Academic Honor
								</div>
								<div class="row__text">
									{{school['academic honor']}}
								</div>
							</div>

							<div 
								class="block__row field-of-study"
								v-if="school['field of study']"
							>
								<div class="row__label iomico-style-lable">
									Field of study
								</div>
								<div class="row__text">
									{{school['field of study']}}
								</div>
							</div>

							<div 
								class="block__row student-activities"
								v-if="school['student activities'] && school['student activities'].length"
							>
								<div class="row__label iomico-style-lable">
									other
								</div>
								<div class="row__text">
									<span 
										class="text__tag"
										v-bind:key="activityIndex"
										v-for="(activity, activityIndex) in school['student activities']"
									>
										{{activity}}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
</template>

<script>

// @ is an alias to /src
import Rating from '@/components/Rating.vue';

// import cvEmpty from '@/assets/data/cvEmpty.json';

export default {
	name: 'profileview',

	components: {
		Rating
	},

	data: function(){
		return {
			profile: {}
			,loading: false
			,errored: false
			,languageLevels: [
				'A1 / Beginner'
				,'A2 / Elementary'
				,'B1 / Intermediate'
				,'B2 / Upper Intermediate'
				,'C1 / Advanced'
				,'C2 / Proficient'
			]
			,familyStatus: [
				'Single'
				,'Married'
			]
			,activeFromTab: 'expertise'
			,imageData: ''
		};
	}

	,computed: {
		getPictureUrl: function(){
			let path = 'http://iomico.co/images/';

			if(this.imageData){ 
				return this.imageData 
			} else{
				return path + (this.profile.picture || 'avatar__600x600.jpg');
			}
		}
	}

	,methods:{

		updateActiveFromTab: function(formTabName){
			this.activeFromTab = formTabName;
			window.scrollTo({top: 0, behavior: 'smooth'}); 
		}

		,getSubExpertise: function(subExpertise){
			return subExpertise.filter(item => item[2].enabled);
		}
		,printing: function(){ window.print(); }
	}

	,created(){
		var id = this.$route.params.id;
		var url = `http://iomico.co/api/profile/${id}/`;

		this.axios
			.get(url)
			.then(response => {
				this.profile = response.data;
			})
			.catch(error => {
				this.errored = true;
			})
			.finally(() => {
				this.loading = false;
			})

	}

	,destroyed(){
	}

	,mounted(){
	}

}
</script>
